<template>
  <div class="label-associate">
    <div class="content-wrapper">
      
      <div class="selection-section">
        <div class="label-text">
          {{ operationType === 'migrate' ? '迁移至标签:' : '关联标签:' }}
          <span class="margin-left-10">
            <el-cascader 
              ref="cascader"
              v-model="selectedLabelId" 
              :placeholder="placeholder || '请选择标签'"
              :props="cascaderProps"
              :disabled="loading"
              clearable
              @change="handleCascaderChange">
            </el-cascader>
          </span>
        </div>
      </div>

      <div class="button-section">
        <el-button @click="handleCancel" :disabled="loading">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleConfirm" 
          :loading="loading"
          :disabled="!selectedLabelId || selectedLabelId.length === 0">
          确定
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { getRelatedOption, updateRelatedTag } from "@/api/waveform/related";
import { waveformNpyResolveUpdate } from "@/api/waveform/manage";

export default {
  name: "LabelAssociate",
  props: {
    // 操作类型: 'relate' 关联, 'migrate' 迁移
    operationType: {
      type: String,
      default: 'relate',
      validator: (value) => ['relate', 'migrate'].includes(value)
    },
    // 记录ID数组
    recordIds: {
      type: Array,
      default: () => []
    },
    // 占位符文本
    placeholder: {
      type: String,
      default: ''
    },
    // 是否显示记录信息
    showRecordInfo: {
      type: Boolean,
      default: true
    },
    // 是否使用懒加载
    lazy: {
      type: Boolean,
      default: true
    },
    // API配置
    apiConfig: {
      type: Object,
      default: () => ({
        getOptions: getRelatedOption,
        updateTag: updateRelatedTag
      })
    }
  },
  data() {
    return {
      selectedLabelId: [],
      selectedLabelNodes: [],
      loading: false,
      cascaderProps: {
        lazy: this.lazy,
        lazyLoad: this.lazyLoad
      }
    };
  },
  methods: {
    // 懒加载函数
    async lazyLoad(node, resolve) {
      try {
        const params = node.level === 0 
          ? { parentId: node.level }
          : { parentId: node.data.value };
          
        const res = await this.apiConfig.getOptions(params);
        
        if (res.code === 200) {
          const nodes = res.data || [];
          resolve(nodes);
        } else {
          this.$message.error(res.msg || '加载标签数据失败');
          resolve([]);
        }
      } catch (error) {
        console.error('加载标签数据失败:', error);
        this.$message.error('加载标签数据失败');
        resolve([]);
      }
    },

    // 处理级联选择器变化
    handleCascaderChange(value) {
      if (!value || value.length === 0) {
        this.selectedLabelNodes = [];
        return;
      }

      const cascader = this.$refs.cascader;
      if (cascader) {
        const nodes = cascader.getCheckedNodes();
        if (nodes && nodes.length > 0) {
          this.selectedLabelNodes = nodes[0].pathNodes;
        }
      }
    },
    
    // 处理取消操作
    handleCancel() {
      this.$emit("close");
    },
    
    // 处理确认操作
    async handleConfirm() {
      if (!this.selectedLabelId || this.selectedLabelId.length === 0) {
        this.$message.warning('请选择标签');
        return;
      }
      
      if (!this.recordIds || this.recordIds.length === 0) {
        this.$message.warning('没有可操作的记录');
        return;
      }

      this.loading = true;
      
      try {
        // 获取选择的标签ID（通常是级联选择的最后一个值）
        const targetLabelId = this.selectedLabelId[this.selectedLabelId.length - 1];
        
        let params = {};
        let apiCall = null;
        
        // 根据操作类型选择不同的API接口和参数
        if (this.operationType === 'migrate') {
          // 迁移操作：调用updateRelatedTag接口，传参保持当前格式
          params = {
            recordIds: this.recordIds,
            relatedId: targetLabelId
          };
          apiCall = this.apiConfig.updateTag;
        } else if (this.operationType === 'relate') {
          // 关联操作：调用waveformNpyResolveUpdate接口
          // 从 selectedLabelNodes 获取一级和二级的 value 和 label
          let targetDictItemId = '';
          let targetDictItemName = '';
          let targetWaveformTagId = '';
          let targetWaveformTagName = '';

          if (this.selectedLabelNodes && this.selectedLabelNodes.length >= 1) {
            // 一级数据
            targetDictItemId = this.selectedLabelNodes[0].value;
            targetDictItemName = this.selectedLabelNodes[0].label;
          }

          if (this.selectedLabelNodes && this.selectedLabelNodes.length >= 2) {
            // 二级数据
            targetWaveformTagId = this.selectedLabelNodes[1].value;
            targetWaveformTagName = this.selectedLabelNodes[1].label;
          }

          params = {
            waveformNpyIds: this.recordIds,
            targetDictItemId: targetDictItemId,
            targetDictItemName: targetDictItemName,
            targetWaveformTagId: targetWaveformTagId,
            targetWaveformTagName: targetWaveformTagName
          };
          apiCall = waveformNpyResolveUpdate;
        }
        
        if (!apiCall) {
          this.$message.error('不支持的操作类型');
          return;
        }
        
        const res = await apiCall(params);
        
        if (res.code === 200) {
          this.$message({
            message: res.msg,
            type: "success",
          });
          
          await new Promise(resolve => setTimeout(resolve, 500));
          
          this.$emit("submit", {
            recordIds: this.recordIds,
            targetLabelId: targetLabelId,
            operationType: this.operationType
          });
        } else {
          this.$message({
            message: res.msg,
            type: "error",
          });
        }
      } catch (error) {
        console.error('操作失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    // 重置组件状态
    reset() {
      this.selectedLabelId = [];
      this.loading = false;
    }
  },
  
  watch: {
    // 监听弹窗显示状态，显示时重置数据
    visible(val) {
      if (val) {
        this.reset();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.label-associate {
  .content-wrapper {
    
    .selection-section {
      margin-bottom: 20px;
      
      .label-text {
        font-size: 14px;
        color: #606266;
        
        .margin-left-10 {
          margin-left: 10px;
        }
      }
    }
    
    .button-section {
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
      padding-top: 20px;
      min-height: 40px;
      
      .el-button {
        margin-left: 10px;
        margin-bottom: 0;
      }
    }
  }
}
</style>